import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Twitter, Youtube, MapPin, Phone, Mail, ArrowRight, CheckCircle2 } from 'lucide-react';
import { FOOTER } from '@/constants/testIds';
import { CONTACT_INFO } from '@/lib/content';
import { subscribeEmail } from '@/lib/api';

export default function Footer() {
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState('idle');
    const [error, setError] = useState('');

    const onSubscribe = async (e) => {
        e.preventDefault();
        setStatus('loading');
        setError('');
        try {
            await subscribeEmail(email);
            setStatus('success');
            setEmail('');
        } catch (err) {
            setStatus('error');
            setError(err?.response?.data?.detail?.[0]?.msg || 'Please enter a valid email.');
        }
    };

    return (
        <footer data-testid={FOOTER.root} className="relative">
            {/* Get In Touch banner — mustard */}
            <section className="bg-brand-mustard text-brand-navy">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12 md:py-16 lg:py-20 grid lg:grid-cols-2 gap-10 items-center">
                    <div>
                        <div className="text-xs uppercase tracking-[0.35em] font-bold text-brand-navy/70 mb-3">
                            Get In Touch
                        </div>
                        <h2 className="font-display font-extrabold text-5xl md:text-6xl tracking-tight leading-[0.95] text-brand-navy">
                            Let’s build the campaign<br />
                            <span className="italic font-medium text-brand-navy/75">that moves units.</span>
                        </h2>
                        <Link
                            to="/contact"
                            data-testid={FOOTER.contactCtaLink}
                            className="inline-flex items-center gap-3 mt-8 bg-brand-navy text-white px-7 py-4 text-xs font-bold uppercase tracking-[0.25em] hover:bg-brand-orange transition-colors"
                        >
                            Contact Us <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>

                    <div className="lg:pl-10 lg:border-l lg:border-brand-navy/20">
                        <div className="text-xs uppercase tracking-[0.35em] font-bold text-brand-navy/70 mb-3">
                            Subscribe
                        </div>
                        <p className="text-brand-navy/85 mb-6 max-w-md">
                            Quarterly playbook drops, fresh creative work, and broadcast trends — sent straight to your inbox. No spam, ever.
                        </p>
                        {status === 'success' ? (
                            <div
                                data-testid={FOOTER.subscribeSuccess}
                                className="inline-flex items-center gap-2 bg-brand-navy text-white px-5 py-4 text-sm font-medium"
                            >
                                <CheckCircle2 className="w-5 h-5 text-brand-mustard" />
                                You’re on the list — talk soon.
                            </div>
                        ) : (
                            <form
                                data-testid={FOOTER.subscribeForm}
                                onSubmit={onSubscribe}
                                className="flex flex-col sm:flex-row gap-3 max-w-lg"
                            >
                                <input
                                    type="email"
                                    required
                                    placeholder="you@dealership.com"
                                    value={email}
                                    data-testid={FOOTER.subscribeInput}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="flex-1 bg-white border border-brand-navy/20 text-brand-navy placeholder:text-brand-navy/40 px-5 py-4 focus:outline-none focus:border-brand-navy"
                                />
                                <button
                                    type="submit"
                                    disabled={status === 'loading'}
                                    data-testid={FOOTER.subscribeButton}
                                    className="bg-brand-navy text-white px-7 py-4 text-xs font-bold uppercase tracking-[0.25em] hover:bg-brand-orange transition-colors disabled:opacity-60"
                                >
                                    {status === 'loading' ? 'Sending…' : 'Subscribe'}
                                </button>
                            </form>
                        )}
                        {status === 'error' && (
                            <p className="text-brand-navy mt-3 text-sm font-medium">{error}</p>
                        )}
                    </div>
                </div>
            </section>

            {/* Footer details — deep navy */}
            <div className="bg-brand-navy text-white">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16">
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
                        <div className="lg:col-span-1">
                            <div className="font-display font-extrabold text-xl text-white">CFL Ad Group</div>
                            <div className="text-[10px] uppercase tracking-[0.3em] text-brand-orange mt-1 font-bold">
                                Direct Response Advertising
                            </div>
                            <p className="text-white/65 leading-relaxed text-sm mt-5">
                                Three decades producing direct response advertising for automotive dealerships
                                and ambitious brands across traditional television, CTV/OTT, and social.
                            </p>
                        </div>

                        <div>
                            <div className="text-[11px] uppercase tracking-[0.3em] text-brand-orange font-bold mb-4">
                                Address
                            </div>
                            <div className="flex gap-3 text-white/80">
                                <MapPin className="w-4 h-4 text-brand-orange shrink-0 mt-1" />
                                <div className="leading-relaxed text-sm">
                                    {CONTACT_INFO.address1}<br />
                                    {CONTACT_INFO.address2}<br />
                                    {CONTACT_INFO.address3}
                                </div>
                            </div>
                        </div>

                        <div>
                            <div className="text-[11px] uppercase tracking-[0.3em] text-brand-orange font-bold mb-4">
                                Reach Us
                            </div>
                            <a
                                href={CONTACT_INFO.phoneHref}
                                className="flex items-center gap-2 text-white/85 hover:text-white text-sm mb-2"
                            >
                                <Phone className="w-4 h-4 text-brand-orange" />
                                {CONTACT_INFO.phone}
                            </a>
                            <a
                                href={CONTACT_INFO.emailHref}
                                className="flex items-center gap-2 text-white/85 hover:text-white text-sm break-all"
                            >
                                <Mail className="w-4 h-4 text-brand-orange shrink-0" />
                                {CONTACT_INFO.email}
                            </a>
                        </div>

                        <div>
                            <div className="text-[11px] uppercase tracking-[0.3em] text-brand-orange font-bold mb-4">
                                Follow Us
                            </div>
                            <div className="flex items-center gap-2">
                                <a
                                    href="https://www.facebook.com/"
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid={FOOTER.socialFacebook}
                                    aria-label="Facebook"
                                    className="inline-flex h-10 w-10 items-center justify-center border border-white/15 text-white/70 hover:text-white hover:border-brand-orange hover:bg-brand-orange transition-colors"
                                >
                                    <Facebook className="w-4 h-4" />
                                </a>
                                <a
                                    href="https://x.com/"
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid={FOOTER.socialTwitter}
                                    aria-label="X (Twitter)"
                                    className="inline-flex h-10 w-10 items-center justify-center border border-white/15 text-white/70 hover:text-white hover:border-brand-orange hover:bg-brand-orange transition-colors"
                                >
                                    <Twitter className="w-4 h-4" />
                                </a>
                                <a
                                    href="https://www.youtube.com/@cfladgroup8350/videos"
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid={FOOTER.socialYoutube}
                                    aria-label="YouTube"
                                    className="inline-flex h-10 w-10 items-center justify-center border border-white/15 text-white/70 hover:text-white hover:border-brand-orange hover:bg-brand-orange transition-colors"
                                >
                                    <Youtube className="w-4 h-4" />
                                </a>
                            </div>
                        </div>
                    </div>

                    <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/45">
                        <div>© {new Date().getFullYear()} CFL Ad Group. All rights reserved.</div>
                        <div className="font-mono uppercase tracking-[0.25em]">
                            Direct Response · Broadcast · OTT · Social
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
}
